<script lang="ts">
  import { onMount } from 'svelte';
  import { parseRoute, detectDevice } from './lib/route';
  import { loadModel, UnsupportedVersionError, EmptyModelError } from './core/WvmLoader';
  import { ApiError, postView } from './lib/api/client';
  import type { WvmDocument } from './lib/types/wvm';
  import Viewer from './ui/Viewer.svelte';
  import GalleryPage from './GalleryPage.svelte';
  import { t } from './i18n/index.svelte';
  import demoDoc from './fixtures/demo.wvm.json';

  type Status = 'noToken' | 'loading' | 'ready' | 'error';
  type ErrorKind = 'private' | 'notfound' | 'version' | 'empty' | 'network';

  const route = parseRoute();

  let status = $state<Status>('loading');
  let errorKind = $state<ErrorKind>('network');
  let doc = $state<WvmDocument | null>(null);

  function classify(e: unknown): ErrorKind {
    if (e instanceof UnsupportedVersionError) return 'version';
    if (e instanceof EmptyModelError) return 'empty';
    // Model private (03a trả 403): không có key = chưa được phép xem → màn "riêng tư";
    // có key mà vẫn 403 = key sai/đã đổi → coi như "không tìm thấy" (link Author không hợp lệ).
    if (e instanceof ApiError && e.status === 403) return route.authKey ? 'notfound' : 'private';
    if (e instanceof ApiError && e.status === 404) return 'notfound';
    return 'network';
  }

  async function load(token: string) {
    status = 'loading';
    try {
      doc = await loadModel(token, route.authKey);
      status = 'ready';
      postView(token, detectDevice(), route.authKey);
    } catch (e) {
      errorKind = classify(e);
      status = 'error';
    }
  }

  onMount(() => {
    // Trang gallery công khai (/v/{token}/gallery): không tải model 3D — GalleryPage tự fetch ảnh.
    if (route.gallery && route.token) return;
    if (route.demo) {
      doc = demoDoc as unknown as WvmDocument;
      status = 'ready';
      return;
    }
    if (!route.token) {
      status = 'noToken';
      return;
    }
    load(route.token);
  });
</script>

{#if route.gallery && route.token}
  <GalleryPage token={route.token} authKey={route.authKey} />
{:else if status === 'ready' && doc}
  <Viewer
    {doc}
    token={route.token ?? (route.demo ? 'demo-token' : null)}
    authKey={route.authKey}
    embed={route.embed}
    airender={route.airender}
    initialPartId={route.partId}
  />
{:else}
  <main class="state-shell">
    {#if status === 'loading'}
      <div class="loading">
        <div class="spinner" aria-hidden="true"></div>
        <p>{t('app.loading')}</p>
      </div>
    {:else if status === 'noToken'}
      <div class="card">
        <h1>Wood Viewer</h1>
        <p>{t('app.noToken')}</p>
      </div>
    {:else if status === 'error'}
      <div class="card">
        <h1>{t(`err.${errorKind}.title`)}</h1>
        <p>{t(`err.${errorKind}.body`)}</p>
        {#if errorKind === 'network'}
          <button class="retry" onclick={() => route.token && load(route.token)}>{t('app.retry')}</button>
        {/if}
      </div>
    {/if}
  </main>
{/if}

<style>
  .state-shell {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: linear-gradient(var(--color-canvas-top), var(--color-canvas-bottom));
    padding: var(--space-lg);
  }

  .card {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    padding: var(--space-xl);
    max-width: 380px;
    text-align: center;
  }
  h1 {
    margin: 0 0 var(--space-xs);
    font-size: var(--font-size-title);
    color: var(--color-navy);
  }
  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.5;
  }

  .retry {
    margin-top: var(--space-md);
    min-height: var(--size-touch-min);
    padding: 0 var(--space-lg);
    border: none;
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  .retry:hover {
    background: var(--color-primary-accent);
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    color: var(--color-text-secondary);
  }
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
